<template>
  <div>
    <el-dialog
      append-to-body
      top="50px"
      title="片源适配配置"
      :visible.sync="dialogVisible"
      width="60%"
      :before-close="handleClose"
    >
      <div v-loading="loading">
        <div class="toolbar">
          <span class="tip-text">
            说明：c=1 按域名匹配（每日定时自动剔除失效域名），c=2 按路径匹配；每条规则包含 c、u、o 字段，o 子项包含 f、t 及配置项。
          </span>
          <div>
            <el-button size="mini" @click="fillTemplate">填入示例模板</el-button>
            <el-button size="mini" type="primary" plain @click="formatRawJson">格式化 JSON</el-button>
          </div>
        </div>

        <el-tabs v-model="activeTab" type="border-card">
          <el-tab-pane label="编辑原始配置（raw）" name="raw">
            <el-input
              type="textarea"
              :rows="14"
              placeholder="请输入片源配置 JSON 数组，例如：[{&quot;c&quot;:1,&quot;u&quot;:[&quot;scgchc.com&quot;],&quot;o&quot;:[{&quot;f&quot;:1,&quot;t&quot;:1,&quot;user_agent&quot;:&quot;Mozilla/5.0&quot;}]}]"
              v-model="rawConfigStr"
            />
          </el-tab-pane>
          <el-tab-pane label="当前线上生效配置（active，只读预览）" name="active">
            <div class="active-tip">
              展示经每日定时任务剔除失效域名后实际下发的配置，可与「原始配置」对比哪些失效域名已被自动清理。
            </div>
            <el-input
              type="textarea"
              :rows="14"
              readonly
              placeholder="暂无生效配置"
              v-model="activeConfigStr"
            />
          </el-tab-pane>
        </el-tabs>
      </div>

      <div class="dialog-footer">
        <el-button @click="$emit('input', false)">取 消</el-button>
        <el-button type="primary" :loading="saving" @click="handleSave">保 存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getPlayerUrlConfig, savePlayerUrlConfig } from "@/api/apps/playConfig/strategyConfig";

const EXAMPLE_TEMPLATE = [
  {
    c: 1,
    u: ["scgchc.com"],
    o: [
      {
        f: 1,
        t: 1,
        user_agent: "Mozilla/5.0 (Windows NT 6.1; WOW64)"
      },
      {
        f: 1,
        t: 1,
        referer: "https://fscgc.scgchc.com"
      }
    ]
  },
  {
    c: 1,
    u: ["pan.baidu.com", "mcloud.139.com"],
    o: [
      {
        f: 1,
        t: 1,
        user_agent: "VLC/2.2.6 LibVLC/2.2.6"
      }
    ]
  },
  {
    c: 2,
    u: ["/nby/m3u8/play/api", "/nby/m3u8/getM3u8"],
    o: [
      {
        f: 1,
        t: 1,
        user_agent: "VLC/2.2.6 LibVLC/2.2.6"
      }
    ]
  }
];

export default {
  name: "PlayerUrlConfig",
  props: {
    value: {
      type: Boolean,
      default: () => false
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      activeTab: "raw",
      rawConfigStr: "[]",
      activeConfigStr: "[]"
    };
  },
  computed: {
    dialogVisible() {
      return this.value;
    }
  },
  created() {
    this.loadConfig();
  },
  methods: {
    loadConfig() {
      this.loading = true;
      getPlayerUrlConfig()
        .then(res => {
          this.loading = false;
          if (res && res.data && res.data.code == 200 && res.data.data) {
            const payload =
              typeof res.data.data === "string"
                ? JSON.parse(res.data.data)
                : res.data.data;
            const dataObj = (payload && payload.data) || payload || {};
            const rawArr = Array.isArray(dataObj.raw) ? dataObj.raw : [];
            const activeArr = Array.isArray(dataObj.active) ? dataObj.active : [];
            this.rawConfigStr = JSON.stringify(rawArr, null, 2);
            this.activeConfigStr = JSON.stringify(activeArr, null, 2);
          } else {
            const msg = (res && res.data && res.data.msg) || "获取片源配置失败";
            this.$message.error(msg);
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
    formatRawJson() {
      try {
        const parsed = JSON.parse(this.rawConfigStr || "[]");
        this.rawConfigStr = JSON.stringify(parsed, null, 2);
      } catch (e) {
        this.$message.error("JSON 格式错误：" + e.message);
      }
    },
    fillTemplate() {
      this.rawConfigStr = JSON.stringify(EXAMPLE_TEMPLATE, null, 2);
      this.activeTab = "raw";
    },
    validateConfig(jsonStr) {
      let parsed;
      try {
        parsed = JSON.parse(jsonStr);
      } catch (e) {
        this.$message.error("JSON 解析失败，请检查语法：" + e.message);
        return null;
      }
      if (!Array.isArray(parsed)) {
        this.$message.error("片源配置顶层必须是 JSON 数组（Array）");
        return null;
      }
      for (let i = 0; i < parsed.length; i++) {
        const item = parsed[i];
        if (!item || typeof item !== "object" || Array.isArray(item)) {
          this.$message.error(`第 ${i + 1} 项必须是 JSON 对象`);
          return null;
        }
        const requiredKeys = ["c", "u", "o"];
        for (const k of requiredKeys) {
          if (item[k] === undefined || item[k] === null) {
            this.$message.error(`第 ${i + 1} 项缺少必填字段 "${k}"`);
            return null;
          }
        }
        if (!Array.isArray(item.o) || item.o.length === 0) {
          this.$message.error(`第 ${i + 1} 项的 "o" 必须是非空数组`);
          return null;
        }
        for (let j = 0; j < item.o.length; j++) {
          const opt = item.o[j];
          if (!opt || typeof opt !== "object" || Array.isArray(opt)) {
            this.$message.error(`第 ${i + 1} 项的第 ${j + 1} 个 o 配置必须是 JSON 对象`);
            return null;
          }
          for (const ok of ["f", "t"]) {
            if (opt[ok] === undefined || opt[ok] === null) {
              this.$message.error(`第 ${i + 1} 项的第 ${j + 1} 个 o 配置缺少必填字段 "${ok}"`);
              return null;
            }
          }
        }
      }
      return parsed;
    },
    handleSave() {
      const parsed = this.validateConfig(this.rawConfigStr);
      if (!parsed) {
        return;
      }
      const compactJsonStr = JSON.stringify(parsed);
      const urlsBase64 = window.btoa(unescape(encodeURIComponent(compactJsonStr)));
      this.saving = true;
      savePlayerUrlConfig({ urlsBase64 })
        .then(res => {
          this.saving = false;
          if (res && res.data && res.data.code == 200) {
            this.$message.success("保存成功");
            this.loadConfig();
          } else {
            const msg = (res && res.data && res.data.msg) || "保存失败";
            this.$message.error(msg);
          }
        })
        .catch(() => {
          this.saving = false;
        });
    },
    handleClose() {
      this.$confirm("确认关闭？")
        .then(_ => {
          this.$emit("input", false);
        })
        .catch(_ => {});
    }
  }
};
</script>

<style scoped lang="scss">
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.tip-text {
  font-size: 12px;
  color: #606266;
}

.active-tip {
  font-size: 12px;
  color: #909399;
  margin-bottom: 8px;
}

.dialog-footer {
  text-align: center;
  padding: 15px 0 5px;
}
</style>
